import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useSessionContext } from '@/context/SessionContextProvider';
import { useStoreAuth } from '@/context/StoreAuthContext';
import { useAllChannelUserBindings } from './useChannelUsers';

/**
 * Hook leve para contar conversas com mensagens não lidas no sidebar.
 * Mesma regra do Chat: conversas abertas, não arquivadas, de canais ativos
 * aos quais o usuário tem acesso.
 */
export const useUnreadCount = () => {
  const { networkId } = useSessionContext();
  const { user } = useStoreAuth();
  const { data: allBindings = [] } = useAllChannelUserBindings();

  const restrictedKey = useMemo(() => {
    if (!user?.id || allBindings.length === 0) return '';
    const byChannel = new Map<string, string[]>();
    for (const b of allBindings) {
      const list = byChannel.get(b.channel_id) || [];
      list.push(b.user_id);
      byChannel.set(b.channel_id, list);
    }
    const blocked: string[] = [];
    for (const [chId, users] of byChannel) {
      if (users.length > 0 && !users.includes(user.id)) blocked.push(chId);
    }
    return blocked.sort().join(',');
  }, [allBindings, user?.id]);

  const { data: unreadData, refetch } = useQuery({
    queryKey: ['crm-unread-count-v2', networkId, user?.id, restrictedKey],
    queryFn: async () => {
      if (!networkId) return { total: 0, byChannel: {} as Record<string, number> };
      const blocked = new Set(restrictedKey ? restrictedKey.split(',') : []);
      let count = 0;
      const byChannel: Record<string, number> = {};
      const PAGE = 1000;
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await (supabase as any)
          .from('crm_conversations')
          .select('id, unread_count, channel_id, crm_channels!inner(id, is_active)')
          .eq('network_id', networkId)
          .eq('is_archived', false)
          .eq('status', 'open')
          .gt('unread_count', 0)
          .eq('crm_channels.is_active', true)
          .order('id', { ascending: true })
          .range(from, from + PAGE - 1);
        if (error) return { total: count, byChannel };
        const rows = (data || []) as any[];
        for (const c of rows) {
          if (c.channel_id && blocked.has(c.channel_id)) continue;
          count += 1;
          if (c.channel_id) byChannel[c.channel_id] = (byChannel[c.channel_id] || 0) + 1;
        }
        if (rows.length < PAGE) break;
      }
      return { total: count, byChannel };
    },
    enabled: !!networkId,
    refetchInterval: 30000,
  });

  const totalUnread = unreadData?.total ?? 0;
  const unreadByChannel = unreadData?.byChannel ?? {};
  return { totalUnread, unreadByChannel, refetch };
};
